import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Container from '@ow/ow-components/lib/components/Container';
import Breadcrumbs from '../Breadcrumbs';
import NoSSR from '@ow/ow-components/lib/components/NoSSR';

import styled from 'styled-components';
import { ifProp } from 'styled-tools';
import theme from '@ow/ow-components/lib/theme';

import { Flex, Box } from 'rebass';
import lodashGet from 'lodash.get';
import AddToCart from 'connected-components/components/AddToCart';
import AddToWishlistWrapper from 'connected-components/components/AddToWishlistWrapper';
import Spinner from '@ow/ow-components/lib/components/Spinner';
import Icon from '@ow/ow-components/lib/components/Icon';
import Link from '@ow/ow-components/lib/components/Link';
import SwipeInstruction from '@ow/ow-components/lib/components/SwipeInstruction';
import ToolTip from '@ow/ow-components/lib/components/ToolTip';
import Truncate from 'react-truncate';
import { specsToObject, specValue } from '../../utils/ProductUtils';
import { AVAILABILITY_BYPASSED_PRODUCT_TYPES } from '@ow/ow-components/lib/constants/availability';
import { generateDigitalDataProduct } from 'connected-components/utils/analyticsUtils';
import ProductPrice from 'connected-components/components/ProductPrice';
import CdnImage from 'connected-components/components/CdnImage';

const AddButtonContainer = styled(Box)`
  margin-top: ${theme.space.quarter};
  button {
    width: 100%;
  }
`;

const CompareProductsWrapper = styled.div`
  position: relative;
`;

const CompareProducts = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  border: solid res 3px;
`;

const PRODUCT_TILE_WIDTH = '230px';
const BOX_SHADOW = `${theme.space.half} 0 ${theme.space.standard} -${theme.space.half} rgba(0,0,0,.1)`;

const PageTitle = styled.div`
  padding-bottom: ${theme.space.half};
  text-align: center;

  h1 {
    margin: 0 auto ${theme.space.double};
    padding: 0 ${theme.space.half};
  }
`;

const ProductTileWrapper = styled.div`
  padding-left: ${theme.space.half};
  padding-right: ${theme.space.half};
  width: ${PRODUCT_TILE_WIDTH};
  min-width: ${PRODUCT_TILE_WIDTH};
  background-color: ${theme.colors.white};

  img {
    width: 190px;
    height: 190px;
    margin: auto;
  }

  border-right: solid 1px ${theme.colors.text};
  border-left: ${ifProp(
    { isPrimary: true },
    `solid 1px ${theme.colors.text}`,
    'solid 1px transparent'
  )};

  @media ${theme.mediaSize.largeMax} {
    margin-left: ${ifProp({ isFirstNonPrimary: true }, PRODUCT_TILE_WIDTH, '0')};
    position: ${ifProp({ isPrimary: true }, 'absolute', 'relative')};
    z-index: ${ifProp({ isPrimary: true }, theme.zIndex.dropdown, 'auto')};
    box-shadow: ${ifProp({ isPrimary: true }, BOX_SHADOW, 'none')};
  }
`;

const HeaderWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${props => (props.isPrimary ? '600' : '200')};
  text-decoration: ${props => (props.isPrimary ? 'none' : 'underline')};
  color: ${theme.colors.primary};
  padding-bottom: ${theme.space.standard};
  font-size: 12px;

  span {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

const PrimaryIconWrapper = styled.div`
  padding-right: 6px;

  &::before {
    display: inline-block;
    background: 0 0;
    border: 0;
    content: ${ifProp({ isPrimary: true }, '"★"', '"☆"')};
    color: ${ifProp({ isPrimary: true }, theme.colors.supernova, theme.colors.primary)};
    font-family: Arial, sans-serif;
    font-size: ${theme.fontSizes.lg};
    padding: 0;
    margin: 0;
    height: 100%;
    min-height: 100%;
    text-decoration: none;
  }
`;

const CloseIconWrapper = styled.span`
  &:hover {
    opacity: 0.6;
  }

  opacity: 0.3;
  transition: opacity 0.2s ease;
  cursor: pointer;
  position: relative;
  margin-left: ${theme.space.half};
`;

const ProductName = styled.h3`
  font-family: ${theme.font};
  height: ${theme.space.double};
  overflow: hidden;
  margin: ${theme.space.quarter} 0 0;
  @media ${theme.mediaSize.smallMin} {
    margin-bottom: ${theme.space.half};
  }
`;

const ProductImage = styled(CdnImage)`
  margin: 0 auto;
  display: block;
  max-height: 100%;
  width: auto;
  max-width: 100%;
`;

const SwipeInstructionContainer = styled.div`
  position: absolute;
  left: ${PRODUCT_TILE_WIDTH};
  right: 0;
  top: 0;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.25s;
  pointer-events: none;
  opacity: ${ifProp({ isVisible: true }, '1', '0')};
`;

const ScrollButtonsContainer = styled(SwipeInstructionContainer)`
  display: none;
  @media ${theme.mediaSize.smallMax} {
    opacity: 1;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
`;

const ScrollButton = styled.button`
  pointer-events: auto;
  margin-left: ${theme.space.half};
  margin-right: ${theme.space.half};
  margin-top: 120px;
  background: none;
  border: none;
  svg {
    background-color: ${theme.colors.white};
    border-radius: 50%;
    border: none;
  }
  opacity: ${ifProp({ isActive: true }, '1', '0.5')};
`;

const Header = ({ isPrimary, handleMakePrimary, handleClose }) => (
  <HeaderWrapper isPrimary={isPrimary}>
    <span onClick={handleMakePrimary} data-ref="compare-make-product-primary">
      <PrimaryIconWrapper isPrimary={isPrimary} />
      {isPrimary ? 'Primary Compare' : 'Make Primary'}
    </span>
    <ToolTip position="left" message={'Remove\u00a0this\u00a0item'}>
      <CloseIconWrapper onClick={handleClose} data-ref="compare-remove-product">
        <Icon name="base--icons--cross" width="12" height="12" color={theme.colors.textDefault} />
      </CloseIconWrapper>
    </ToolTip>
  </HeaderWrapper>
);

Header.propTypes = {
  isPrimary: PropTypes.bool,
  handleMakePrimary: PropTypes.func,
  handleClose: PropTypes.func
};

const ProductSpecsLabel = styled.h5`
  padding: 0;
  padding-top: ${theme.space.half};
  font-size: ${theme.fontSizes.md};
`;

const ProductSpecsList = styled.ul`
  margin: 0 -${theme.space.half};
  padding: 0;

  li {
    border-top: 1px solid ${theme.colors.background3};
    padding: ${theme.space.half};
  }
`;

const LoadingProductInfoSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 150px;
`;

const ProductSpecsListItem = styled.li`
  display: block;
  margin: 0;
  padding: 0;

  background-color: ${props => {
    if (props.hover) {
      return theme.colors.tabHover;
    }
    return props.highlight ? theme.colors.alertBackground3 : theme.colors.white;
  }};

  dl {
    margin: 0;
    padding: 0;
    display: block;
  }
  dt {
    min-height: 19px;
    padding: 0;
    margin: 0;
    display: block;
  }

  dt:last-child {
    color: ${theme.colors.primary};
  }
`;

class ProductSpecsListItemContainer extends Component {
  componentDidMount() {
    this.measure();
  }

  componentDidUpdate() {
    this.measure();
  }

  measure() {
    // Removed findDOMNode. See: https://github.com/yannickcr/eslint-plugin-react/issues/678
    if (this.node) {
      const height = Math.ceil(this.node.offsetHeight);
      if (height > this.props.rowHeight) {
        this.props.updateRowHeight({
          group: this.props.group,
          attribute: this.props.attribute,
          height
        });
      }
    }
  }

  render() {
    const {
      attribute,
      value,
      rowHeight,
      highlight,
      hover,
      onMouseEnter,
      onMouseLeave
    } = this.props;

    return (
      <div ref={node => (this.node = node)}>
        <ProductSpecsListItem
          style={{ minHeight: rowHeight }}
          highlight={highlight}
          hover={hover}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <dl>
            <dt>{attribute}</dt>
            <dt>{value}</dt>
          </dl>
        </ProductSpecsListItem>
      </div>
    );
  }
}

ProductSpecsListItemContainer.propTypes = {
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  updateRowHeight: PropTypes.func,
  group: PropTypes.string,
  attribute: PropTypes.string,
  value: PropTypes.string,
  hover: PropTypes.bool,
  highlight: PropTypes.bool,
  rowHeight: PropTypes.number
};

class CompareView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rowsHeight: {},
      hoverGroup: null,
      hoverAttribute: null,
      swipeInstructionVisible: true,
      leftScrollButtonActive: true,
      rightScrollButtonActive: true
    };
    this.hideSwipeInstruction = () => this.setState({ swipeInstructionVisible: false });
    this.handleProductsScroll = this.handleProductsScroll.bind(this);
    this.handleLeftScrollButtonClick = this.handleLeftScrollButtonClick.bind(this);
    this.handleRightScrollButtonClick = this.handleRightScrollButtonClick.bind(this);
  }

  componentDidMount() {
    this.swipeInstructionTimeout = setTimeout(this.hideSwipeInstruction, 3000);
    if (this.productsContainerElement) {
      this.productsContainerElement.addEventListener('scroll', this.handleProductsScroll);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.swipeInstructionTimeout);
    if (this.productsContainerElement) {
      this.productsContainerElement.removeEventListener('scroll', this.handleProductsScroll);
    }
  }

  handleProductsScroll(event) {
    if (event.target.scrollLeft < 10 && this.state.leftScrollButtonActive) {
      this.setState({ leftScrollButtonActive: false });
    }
    if (event.target.scrollLeft >= 10 && !this.state.leftScrollButtonActive) {
      this.setState({ leftScrollButtonActive: true });
    }
    const maxLeft = event.target.scrollWidth - event.target.getBoundingClientRect().width;
    if (event.target.scrollLeft < maxLeft - 10 && !this.state.rightScrollButtonActive) {
      this.setState({ rightScrollButtonActive: true });
    }
    if (event.target.scrollLeft >= maxLeft - 10 && this.state.rightScrollButtonActive) {
      this.setState({ rightScrollButtonActive: false });
    }
  }

  handleLeftScrollButtonClick() {
    this.productsContainerElement.scrollLeft -= 230;
  }

  handleRightScrollButtonClick() {
    this.productsContainerElement.scrollLeft += 230;
  }

  hoverRow(isHovered, group, attribute) {
    if (isHovered) {
      this.setState({ hoverGroup: group, hoverAttribute: attribute });
    } else {
      this.setState({ hoverGroup: null, hoverAttribute: null });
    }
  }

  renderProductSpecs(sku) {
    const productInfo = this.props.compareProductsInfo[sku];
    if (!productInfo || productInfo.isFetching) {
      return (
        <LoadingProductInfoSpinnerContainer>
          <Spinner />
        </LoadingProductInfoSpinnerContainer>
      );
    }

    if (productInfo.isError) {
      return (
        <LoadingProductInfoSpinnerContainer>
          <p>Unable to get product specifications, please try again later</p>
        </LoadingProductInfoSpinnerContainer>
      );
    }

    const DEFAULT_ROW_HEIGHT = 61;
    const getHeight = (group, attribute) => {
      if (this.state.rowsHeight[group]) {
        return this.state.rowsHeight[group][attribute]
          ? this.state.rowsHeight[group][attribute]
          : DEFAULT_ROW_HEIGHT;
      } else {
        return DEFAULT_ROW_HEIGHT;
      }
    };

    const updateRowHeight = ({ group, attribute, height }) => {
      const { rowsHeight } = this.state;
      this.setState({
        rowsHeight: {
          ...rowsHeight,
          [group]: { ...rowsHeight[group], [attribute]: height }
        }
      });
    };

    const shouldHighlightRow = (group, attribute) => {
      const productsInfo = this.props.compareProductsInfo;
      const fetchedSpecs = this.props.compareTray.products
        .filter(p => productsInfo[p.sku] && productsInfo[p.sku].specs)
        .map(p => specsToObject(productsInfo[p.sku].specs));

      const firstProductSpec = fetchedSpecs[0];
      return !fetchedSpecs.every(productSpec => {
        return (
          productSpec[group] &&
          firstProductSpec[group] &&
          productSpec[group][attribute] === firstProductSpec[group][attribute]
        );
      });
    };

    const specsObj = specsToObject(productInfo.specs);
    const groups = Object.keys(this.props.allSpecs);

    return groups.map(group => {
      return (
        <div key={`${sku}_${group}`}>
          <ProductSpecsLabel>{group}</ProductSpecsLabel>
          <ProductSpecsList>
            {Object.keys(this.props.allSpecs[group])
              .sort()
              .map(attribute => (
                <ProductSpecsListItemContainer
                  onMouseEnter={() => this.hoverRow(true, group, attribute)}
                  onMouseLeave={() => this.hoverRow(false)}
                  group={group}
                  key={attribute}
                  attribute={attribute}
                  hover={this.state.hoverGroup === group && this.state.hoverAttribute === attribute}
                  highlight={shouldHighlightRow(group, attribute)}
                  value={specValue(specsObj, group, attribute)}
                  rowHeight={getHeight(group, attribute)}
                  updateRowHeight={updateRowHeight}
                />
              ))}
          </ProductSpecsList>
        </div>
      );
    });
  }

  renderProductImageAndLink(compareItem) {
    const { sku, img } = compareItem;
    const product = this.props.compareProductsInfo[sku];
    if (!product || product.isFetching) {
      return (
        <div>
          <ProductImage src={img} />
          <ProductName>
            <Truncate lines={2}> </Truncate>
          </ProductName>
        </div>
      );
    }
    const productLink = `${product.seoPath}`;
    const productName = product.name;
    const title = `View more details for ${productName}`;
    return (
      <Link type="router-link" url={productLink} title={title}>
        <ProductImage src={img} alt={title} />
        <ProductPrice sku={sku} quantity={1} view="grid" />
        <ProductName>
          <Truncate lines={2}>{productName}</Truncate>
        </ProductName>
      </Link>
    );
  }

  getProductAttribute(sku, id) {
    return lodashGet(this.props, `compareProductsInfo[${sku}].attributes`, []).find(
      attribute => attribute.id === id
    );
  }

  render() {
    const loading = (
      <LoadingProductInfoSpinnerContainer>
        <Spinner />
      </LoadingProductInfoSpinnerContainer>
    );
    return <NoSSR onSSR={loading}>{this.renderMain()}</NoSSR>;
  }

  renderMain() {
    const { isLoggedIn, compareProductsInfo } = this.props;
    if (!this.props.compareTray.products || this.props.compareTray.products.length === 0) {
      return (
        <Container minHeight={400} pt={20} data-ref="compare-page">
          <Flex>
            <Box>
              <Breadcrumbs items={[{ content: 'Home', href: '/' }, { content: 'Compare' }]} />
            </Box>
          </Flex>
          <PageTitle>
            <h1>Product comparison</h1>
            <h3>Sorry! It appears that there are no products to compare.</h3>
            <p>Please select 2 new items to begin your comparison.</p>
          </PageTitle>
        </Container>
      );
    }
    return (
      <Container minHeight={400} pt={20} data-ref="compare-page">
        <Flex>
          <Box>
            <Breadcrumbs items={[{ content: 'Home', href: '/' }, { content: 'Compare' }]} />
          </Box>
        </Flex>
        <PageTitle>
          <h1>Product comparison</h1>
          <p>
            All the differences have been highlighted, making it easy for you to pick the product
            that bests suits your needs!
          </p>
        </PageTitle>

        <CompareProductsWrapper>
          <CompareProducts
            onMouseDown={this.hideSwipeInstruction}
            ref={ele => (this.productsContainerElement = ele)}
          >
            {this.props.compareTray.products.map((item, index) => {
              const product = lodashGet(compareProductsInfo, item.sku, {});
              const { productType, rangedOnline } = product;
              const { productID, dinkus, rating, webHierarchy } = generateDigitalDataProduct(
                product
              );
              const swmsProductAttr = this.getProductAttribute(item.sku, 'swmsProduct');
              const swmsProduct = lodashGet(swmsProductAttr, 'value') === 'Yes';
              const pbsProductAttr = this.getProductAttribute(item.sku, 'pbsProduct');
              const pbsProduct = lodashGet(pbsProductAttr, 'value') === 'Yes';
              const isSpecialProduct =
                AVAILABILITY_BYPASSED_PRODUCT_TYPES.includes(productType) ||
                pbsProduct ||
                swmsProduct;
              return (
                <ProductTileWrapper
                  key={item.sku}
                  data-ref={'compare-product'}
                  isPrimary={index === 0 && this.props.compareTray.products.length > 1}
                  isFirstNonPrimary={index === 1}
                >
                  <Header
                    isPrimary={index === 0}
                    handleMakePrimary={() => {
                      // TODO: make: 'set primary' an action for compareTray in ow-search
                      const trayItems = this.props.compareTray.products;
                      const selected = trayItems.filter(p => p.sku === item.sku)[0];
                      const others = trayItems.filter(p => p.sku !== item.sku);
                      this.props.setTrayItems([selected, ...others]);
                    }}
                    handleClose={() => this.props.removeFromCompare(item.sku)}
                  />
                  {this.renderProductImageAndLink(item)}
                  <AddButtonContainer mt={theme.space.quarter}>
                    <AddToCart
                      sku={item.sku}
                      productType={productType}
                      rangedOnline={rangedOnline}
                      isSpecialProduct={isSpecialProduct}
                      quantity={1}
                      type={'button'}
                      block
                      data-ref="add-to-cart-button"
                      productID={productID}
                      rating={rating}
                      dinkus={dinkus}
                      webHierarchy={webHierarchy}
                    />
                  </AddButtonContainer>
                  <AddButtonContainer mt={theme.space.quarter}>
                    {productType && (
                      <AddToWishlistWrapper
                        product={{
                          sku: item.sku,
                          name: item.name,
                          productType: productType,
                          rangedOnline: rangedOnline
                        }}
                        isLoggedIn={isLoggedIn}
                        rating={rating}
                        dinkus={dinkus}
                        webHierarchy={webHierarchy}
                      />
                    )}
                  </AddButtonContainer>
                  {this.renderProductSpecs(item.sku)}
                </ProductTileWrapper>
              );
            })}
            <SwipeInstructionContainer isVisible={this.state.swipeInstructionVisible}>
              <SwipeInstruction />
            </SwipeInstructionContainer>
            <ScrollButtonsContainer>
              <ScrollButton
                onClick={this.handleLeftScrollButtonClick}
                isActive={this.state.leftScrollButtonActive}
              >
                <Icon
                  name="components--slider--chevron"
                  width="40"
                  height="40"
                  color={
                    this.state.leftScrollButtonActive
                      ? theme.colors.primary
                      : theme.colors.textDefault
                  }
                />
              </ScrollButton>
              <ScrollButton
                onClick={this.handleRightScrollButtonClick}
                isActive={this.state.rightScrollButtonActive}
              >
                <Icon
                  name="components--slider--chevron-inverted"
                  width="40"
                  height="40"
                  color={
                    this.state.rightScrollButtonActive
                      ? theme.colors.primary
                      : theme.colors.textDefault
                  }
                />
              </ScrollButton>
            </ScrollButtonsContainer>
          </CompareProducts>
        </CompareProductsWrapper>
      </Container>
    );
  }
}

CompareView.propTypes = {
  compareTray: PropTypes.object,
  compareProductsInfo: PropTypes.object,
  allSpecs: PropTypes.object,
  removeFromCompare: PropTypes.func,
  setTrayItems: PropTypes.func,
  isLoggedIn: PropTypes.bool
};

export default CompareView;
